/**
 * SSO handoff broker — client side (ADR-0015).
 *
 * Lets a signed-in user open another ecosystem product without
 * re-authenticating, WITHOUT any Cognito hosted UI or auth-domain cookie:
 *
 *   sender (any signed-in app)                    receiver (target app)
 *   ─────────────────────────                     ─────────────────────
 *   navigateWithHandoff()                         /auth/handoff route
 *     → POST /v1/sso/handoff  (one-time code)       → parseHandoffFragment()
 *     → location = <target>/auth/handoff#code=…      → erase fragment
 *                                                     → redeemHandoff()
 *                                                       (CUSTOM_AUTH signIn +
 *                                                        confirmSignIn(code))
 *
 * Security properties the client side must uphold:
 *   - The code travels in the URL FRAGMENT (never query string) so it stays
 *     out of server/CDN logs and Referer headers.
 *   - The receiver erases the fragment via history.replaceState BEFORE the
 *     exchange, so the code never survives in browser history.
 *   - `to` deep-link targets are validated as same-origin relative paths —
 *     otherwise the handoff link would be an open-redirect primitive.
 *   - On ANY failure the sender falls back to a plain navigation and the
 *     receiver falls back to the product's normal unauthenticated state.
 *     A handoff must never leave the user worse off than no handoff.
 *
 * Spec: docs/adr/ADR-0015-cross-app-sso-handoff-broker.md.
 */
import { confirmSignIn, getCurrentUser, signIn } from "aws-amplify/auth";
import { createSsoHandoff } from "./api.js";
export const HANDOFF_PATH = "/auth/handoff";
/**
 * Build the receiver URL. Exported for tests and for callers that need the
 * URL without navigating (e.g. window.open).
 */
export function buildHandoffUrl(targetUrl, ticket, to) {
    const origin = new URL(targetUrl).origin;
    const params = new URLSearchParams({
        code: ticket.code,
        u: ticket.username,
    });
    const safeTo = sanitizeRelativePath(to);
    if (safeTo)
        params.set("to", safeTo);
    return `${origin}${HANDOFF_PATH}#${params.toString()}`;
}
/**
 * True when a click should be left to the browser's default anchor handling
 * (new tab / new window / non-primary button). Callers keep a real
 * `href={targetUrl}` on the anchor and only intercept plain left-clicks:
 *
 * @example
 * ```tsx
 * <a
 *   href={product.url}
 *   onClick={(e) => {
 *     if (shouldBypassHandoff(e)) return; // browser handles cmd/middle-click
 *     e.preventDefault();
 *     void navigateWithHandoff({ apiBase, token, targetProductId: product.id, targetUrl: product.url });
 *   }}
 * >
 * ```
 */
export function shouldBypassHandoff(e) {
    return Boolean(e.defaultPrevented ||
        e.metaKey || e.ctrlKey || e.shiftKey || e.altKey ||
        (typeof e.button === "number" && e.button !== 0));
}
/**
 * Mint a handoff code and navigate the browser to the target product.
 * Falls back to a PLAIN navigation (no SSO) if minting fails for any
 * reason — the target then shows its normal login state, same as today.
 */
export async function navigateWithHandoff(opts) {
    let destination = appendPath(opts.targetUrl, opts.to);
    try {
        const result = await createSsoHandoff(opts.apiBase, opts.token, opts.targetProductId);
        if (result.ok) {
            destination = buildHandoffUrl(opts.targetUrl, result.data, opts.to);
        }
        else {
            console.warn("sso-handoff: mint failed, falling back to plain navigation", result.error);
        }
    }
    catch {
        // network / unexpected — plain navigation fallback
    }
    window.location.assign(destination);
}
function appendPath(targetUrl, to) {
    const safeTo = sanitizeRelativePath(to);
    if (!safeTo)
        return targetUrl;
    return `${new URL(targetUrl).origin}${safeTo}`;
}
/** 32 random bytes, base64url → 43 chars. Allow slack; reject junk early. */
const CODE_RE = /^[A-Za-z0-9_-]{40,64}$/;
const USERNAME_RE = /^[\w.@+-]{1,128}$/;
/**
 * Validate a deep-link path: same-origin relative only. Rejects absolute
 * URLs, protocol-relative (`//evil.com`), backslash tricks, and fragments
 * that don't start with `/` — the open-redirect guard from ADR-0015.
 */
export function sanitizeRelativePath(to) {
    if (!to)
        return undefined;
    if (!to.startsWith("/") || to.startsWith("//") || to.includes("\\")) {
        return undefined;
    }
    return to;
}
/**
 * Parse (and validate) the handoff ticket from a location hash. Returns null
 * when the fragment is not a well-formed handoff — the caller falls back to
 * the normal unauthenticated state.
 */
export function parseHandoffFragment(hash) {
    const raw = hash.startsWith("#") ? hash.slice(1) : hash;
    if (!raw)
        return null;
    const params = new URLSearchParams(raw);
    const code = params.get("code") ?? "";
    const username = params.get("u") ?? "";
    if (!CODE_RE.test(code) || !USERNAME_RE.test(username))
        return null;
    return {
        code,
        username,
        to: sanitizeRelativePath(params.get("to")),
    };
}
/**
 * Redeem a handoff code via the Cognito CUSTOM_AUTH flow. On success Amplify
 * holds a full session for THIS product's app client, exactly as after a
 * normal login. One-shot: the code is burned server-side on first touch, so
 * never retry a failed redemption with the same code.
 */
export async function redeemHandoff(ticket) {
    // If this browser already has a session for this product, don't burn the
    // code on a signIn that Amplify would reject (UserAlreadyAuthenticated).
    try {
        await getCurrentUser();
        return { status: "already_authenticated" };
    }
    catch {
        // no current session — proceed
    }
    try {
        const out = await signIn({
            username: ticket.username,
            options: { authFlowType: "CUSTOM_WITHOUT_SRP" },
        });
        if (out.nextStep.signInStep !== "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
            return { status: "failed", reason: `unexpected_step:${out.nextStep.signInStep}` };
        }
        const confirmed = await confirmSignIn({ challengeResponse: ticket.code });
        if (confirmed.isSignedIn)
            return { status: "signed_in" };
        return { status: "failed", reason: `not_signed_in:${confirmed.nextStep.signInStep}` };
    }
    catch (err) {
        const name = err.name ?? "unknown";
        if (name === "UserAlreadyAuthenticatedException") {
            return { status: "already_authenticated" };
        }
        return { status: "failed", reason: name };
    }
}
